import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'

export default function PaymentPlansPage() {
  const { profile } = useAuth()
  const [clients, setClients] = useState([])
  const [invoices, setInvoices] = useState([])
  const [plans, setPlans] = useState([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [toast, setToast] = useState('')
  const [form, setForm] = useState({ client_id: '', invoice_id: '', total_amount: '', num_installments: 3, start_date: new Date().toISOString().split('T')[0] })
  const [preview, setPreview] = useState([])

  const notify = msg => { setToast(msg); setTimeout(() => setToast(''), 3000) }

  useEffect(() => { load() }, [])

  async function load() {
    const { data: c } = await supabase.from('clients').select('id,full_name,business_name').eq('cpa_id', profile.id)
    setClients(c || [])
    const clientIds = (c || []).map(x => x.id)
    if (clientIds.length) {
      const { data: inv } = await supabase.from('invoices').select('*').in('client_id', clientIds).in('status', ['sent', 'overdue'])
      setInvoices(inv || [])
      const { data: p } = await supabase.from('payment_plans').select('*').in('client_id', clientIds).order('created_at', { ascending: false })
      setPlans(p || [])
    }
    setLoading(false)
  }

  function buildInstallments(total, num, startDate) {
    const amount = parseFloat(total) / num
    const installments = []
    for (let i = 0; i < num; i++) {
      const d = new Date(startDate)
      d.setMonth(d.getMonth() + i)
      installments.push({ number: i + 1, amount: amount.toFixed(2), due_date: d.toISOString().split('T')[0], status: 'pending' })
    }
    return installments
  }

  function updatePreview() {
    if (form.total_amount && form.num_installments && form.start_date) {
      setPreview(buildInstallments(form.total_amount, form.num_installments, form.start_date))
    }
  }

  useEffect(() => { updatePreview() }, [form.total_amount, form.num_installments, form.start_date])

  async function handleSave() {
    if (!form.client_id || !form.total_amount) { notify('Please fill required fields'); return }
    const installments = buildInstallments(form.total_amount, form.num_installments, form.start_date)
    await supabase.from('payment_plans').insert({
      client_id: form.client_id,
      invoice_id: form.invoice_id || null,
      total_amount: parseFloat(form.total_amount),
      installments,
      status: 'active'
    })
    notify('Payment plan created!')
    setShowModal(false)
    load()
  }

  async function markInstallmentPaid(plan, installmentIndex) {
    const updated = plan.installments.map((inst, i) =>
      i === installmentIndex ? { ...inst, status: 'paid', paid_at: new Date().toISOString() } : inst
    )
    const allPaid = updated.every(i => i.status === 'paid')
    await supabase.from('payment_plans').update({
      installments: updated,
      status: allPaid ? 'completed' : 'active'
    }).eq('id', plan.id)
    notify('Installment marked as paid!')
    load()
  }

  async function deletePlan(id) {
    if (!window.confirm('Delete this payment plan?')) return
    await supabase.from('payment_plans').delete().eq('id', id)
    load()
  }

  if (loading) return <div className="skeleton" style={{ height: 200 }} />

  return (
    <div>
      {toast && <div style={{ position: 'fixed', bottom: 24, right: 24, background: '#202326', color: '#CBB17B', padding: '12px 20px', borderRadius: 10, fontSize: 13, fontWeight: 700, zIndex: 9999 }}>{toast}</div>}

      <div className="card" style={{ padding: '24px 28px', marginBottom: 20, background: '#202326', border: 'none' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ color: '#fff', fontSize: 20, marginBottom: 4 }}>Payment Plans</h2>
            <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, margin: 0 }}>Split large invoices into manageable installments for clients.</p>
          </div>
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>+ New Payment Plan</button>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 20 }}>
        {[
          { label: 'Active Plans', value: plans.filter(p => p.status === 'active').length, icon: '📋' },
          { label: 'Total Outstanding', value: `$${plans.filter(p => p.status === 'active').reduce((s, p) => s + p.installments.filter(i => i.status === 'pending').reduce((ss, i) => ss + parseFloat(i.amount), 0), 0).toFixed(2)}`, icon: '⏳' },
          { label: 'Completed Plans', value: plans.filter(p => p.status === 'completed').length, icon: '✅' },
        ].map(s => (
          <div key={s.label} className="card" style={{ padding: '18px 20px', display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ fontSize: 28 }}>{s.icon}</div>
            <div>
              <div style={{ fontSize: 22, fontWeight: 800, color: '#202326', fontFamily: "'DM Sans', sans-serif" }}>{s.value}</div>
              <div style={{ fontSize: 12, color: '#62615D' }}>{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {plans.length === 0 ? (
        <div className="card" style={{ padding: 48, textAlign: 'center', color: '#62615D', fontSize: 13 }}>No payment plans yet. Create one to help clients pay large bills over time!</div>
      ) : plans.map(plan => {
        const client = clients.find(c => c.id === plan.client_id)
        const paid = plan.installments.filter(i => i.status === 'paid').reduce((s, i) => s + parseFloat(i.amount), 0)
        const remaining = plan.total_amount - paid
        const progress = (paid / plan.total_amount) * 100

        return (
          <div key={plan.id} className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: 16 }}>
            <div style={{ padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border)', background: '#F7F3EB' }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#202326' }}>{client?.business_name || client?.full_name}</div>
                <div style={{ fontSize: 12, color: '#62615D', marginTop: 2 }}>Total: ${plan.total_amount.toFixed(2)} · {plan.installments.length} installments</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: plan.status === 'completed' ? '#D1FAE5' : '#DBEAFE', color: plan.status === 'completed' ? '#065F46' : '#1E40AF' }}>
                  {plan.status === 'completed' ? '✅ Completed' : '🔄 Active'}
                </span>
                <button onClick={() => deletePlan(plan.id)} className="btn btn-ghost" style={{ fontSize: 11, color: '#EF4444', padding: '5px 10px' }}>Delete</button>
              </div>
            </div>

            {/* Progress bar */}
            <div style={{ padding: '12px 20px', borderBottom: '1px solid var(--border)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#62615D', marginBottom: 6 }}>
                <span>Paid: <strong style={{ color: '#10B981' }}>${paid.toFixed(2)}</strong></span>
                <span>Remaining: <strong style={{ color: '#F59E0B' }}>${remaining.toFixed(2)}</strong></span>
              </div>
              <div style={{ height: 8, background: '#F0EBE0', borderRadius: 4, overflow: 'hidden' }}>
                <div style={{ height: '100%', background: '#10B981', width: `${progress}%`, borderRadius: 4, transition: 'width 0.5s ease' }} />
              </div>
            </div>

            {/* Installments */}
            {plan.installments.map((inst, i) => (
              <div key={i} style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 14, borderBottom: i < plan.installments.length - 1 ? '1px solid var(--border)' : 'none' }}>
                <div style={{ width: 28, height: 28, borderRadius: '50%', background: inst.status === 'paid' ? '#D1FAE5' : '#F7F3EB', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: inst.status === 'paid' ? '#065F46' : '#62615D', flexShrink: 0 }}>
                  {inst.status === 'paid' ? '✓' : inst.number}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#202326' }}>Installment {inst.number} — ${inst.amount}</div>
                  <div style={{ fontSize: 11, color: '#62615D' }}>Due: {new Date(inst.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</div>
                </div>
                <div>
                  {inst.status === 'paid' ? (
                    <span style={{ fontSize: 12, color: '#10B981', fontWeight: 700 }}>✅ Paid {inst.paid_at ? new Date(inst.paid_at).toLocaleDateString() : ''}</span>
                  ) : plan.status !== 'completed' && (
                    <button onClick={() => markInstallmentPaid(plan, i)} className="btn btn-ghost" style={{ fontSize: 11, padding: '5px 12px', color: '#10B981', border: '1px solid #10B981' }}>
                      Mark Paid
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )
      })}

      {/* Modal */}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
          onClick={e => { if (e.target === e.currentTarget) setShowModal(false) }}>
          <div className="card animate-fadeUp" style={{ width: '100%', maxWidth: 520, maxHeight: '90vh', overflow: 'auto', padding: 0 }}>
            <div style={{ padding: '18px 22px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, background: '#fff', zIndex: 10 }}>
              <h2 style={{ fontSize: 17 }}>New Payment Plan</h2>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', fontSize: 20, color: '#62615D', cursor: 'pointer' }}>×</button>
            </div>
            <div style={{ padding: 22 }}>
              <div className="form-group">
                <label className="label">Client *</label>
                <select className="input" value={form.client_id} onChange={e => setForm(f => ({ ...f, client_id: e.target.value, invoice_id: '' }))}>
                  <option value="">Select client...</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.business_name || c.full_name}</option>)}
                </select>
              </div>

              {form.client_id && invoices.filter(i => i.client_id === form.client_id).length > 0 && (
                <div className="form-group">
                  <label className="label">Link to Invoice (optional)</label>
                  <select className="input" value={form.invoice_id} onChange={e => {
                    const inv = invoices.find(i => i.id === e.target.value)
                    setForm(f => ({ ...f, invoice_id: e.target.value, total_amount: inv ? inv.total_amount : f.total_amount }))
                  }}>
                    <option value="">No invoice linked</option>
                    {invoices.filter(i => i.client_id === form.client_id).map(inv => (
                      <option key={inv.id} value={inv.id}>{inv.invoice_number} — ${inv.total_amount}</option>
                    ))}
                  </select>
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group">
                  <label className="label">Total Amount *</label>
                  <input className="input" type="number" placeholder="0.00" value={form.total_amount} onChange={e => setForm(f => ({ ...f, total_amount: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="label">Number of Installments</label>
                  <select className="input" value={form.num_installments} onChange={e => setForm(f => ({ ...f, num_installments: parseInt(e.target.value) }))}>
                    {[2,3,4,6,12].map(n => <option key={n} value={n}>{n} payments</option>)}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label className="label">First Payment Date</label>
                <input className="input" type="date" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} />
              </div>

              {preview.length > 0 && (
                <div style={{ background: '#F7F3EB', borderRadius: 10, padding: '14px 16px', marginBottom: 16 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#202326', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>Payment Schedule Preview</div>
                  {preview.map((inst, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '5px 0', borderBottom: i < preview.length - 1 ? '1px solid #DED6CA' : 'none' }}>
                      <span style={{ color: '#62615D' }}>Installment {inst.number} — {new Date(inst.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                      <span style={{ fontWeight: 700, color: '#202326' }}>${inst.amount}</span>
                    </div>
                  ))}
                </div>
              )}

              <div style={{ display: 'flex', gap: 10 }}>
                <button className="btn btn-ghost" onClick={() => setShowModal(false)} style={{ flex: 1, justifyContent: 'center' }}>Cancel</button>
                <button className="btn btn-primary" onClick={handleSave} style={{ flex: 2, justifyContent: 'center' }}>Create Payment Plan</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
